import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse
} from '@mui/material';
import { 
  Check as CheckIcon,
  School,
  Business,
  Group,
  RecordVoiceOver,
  Psychology,
  VideoCall,
  Timeline,
  CastForEducation,
  EmojiPeople,
  ExpandMore,
  MenuBook,
  Workspaces,
  SupportAgent,
  Assessment
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import PageHeader from '../../components/PageHeader';
import { useTheme, alpha } from '@mui/material/styles';

const Lesgeven = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = translations[language].lesgevenPage;
  const theme = useTheme();
  const [expandedServices, setExpandedServices] = useState<{[key: string]: boolean}>({});

  const listItemStyle = {
    mb: 1,
    display: 'flex',
    alignItems: 'flex-start'
  };

  const checkIconStyle = {
    color: 'primary.main',
    mr: 1.5,
    mt: 0.5,
    fontSize: '1.2rem'
  };

  // Map icons to each offer item
  const offerIcons = [
    <School sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <Business sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <RecordVoiceOver sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <Group sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <Psychology sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <CastForEducation sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <Group sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <VideoCall sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <School sx={{ color: 'primary.main', fontSize: '1.8rem' }} />,
    <Business sx={{ color: 'primary.main', fontSize: '1.8rem' }} />
  ];

  // Benefits with descriptions
  const benefits = [
    {
      title: language === 'nl' ? 'Betrokken medewerkers' : 'Engaged employees',
      description: language === 'nl' 
        ? 'Interactieve sessies die uw team inspireren en motiveren, resulterend in hogere betrokkenheid en productiviteit.'
        : 'Interactive sessions that inspire and motivate your team, resulting in higher engagement and productivity.',
      icon: <Group color="primary" sx={{ mr: 1.5, fontSize: '1.8rem' }} />
    },
    {
      title: language === 'nl' ? 'Meetbare resultaten' : 'Measurable results',
      description: language === 'nl' 
        ? 'Doelgerichte trainingen met concrete leerdoelen en meetbare uitkomsten die aansluiten bij uw organisatiedoelen.'
        : 'Targeted training with concrete learning objectives and measurable outcomes that align with your organizational goals.',
      icon: <Timeline color="primary" sx={{ mr: 1.5, fontSize: '1.8rem' }} />
    },
    {
      title: language === 'nl' ? 'Op maat gemaakt' : 'Customized delivery',
      description: language === 'nl' 
        ? 'Volledig aangepast aan de specifieke behoeften, cultuur en doelstellingen van uw organisatie.'
        : 'Fully tailored to the specific needs, culture, and objectives of your organization.',
      icon: <CastForEducation color="primary" sx={{ mr: 1.5, fontSize: '1.8rem' }} />
    }
  ];

  const toggleExpand = (service: string) => {
    setExpandedServices(prev => ({
      ...prev,
      [service]: !prev[service]
    }));
  };

  return (
    <>
      <PageHeader title={t.title} subtitle={t.subtitle} />
      
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ mb: 6 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body1" fontWeight="medium">
                {language === 'nl' ? 'Wat bied ik als docent en spreker?' : 'What do I offer as a teacher and speaker?'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                {t.description}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* "Why Me" Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <Button
            variant="contained"
            color="warning"
            size="large"
            onClick={() => navigate('/waarom-ik#host')}
            startIcon={<EmojiPeople />}
            sx={{ px: 4, py: 1.2 }}
          >
            {language === 'nl' ? 'Waarom mij kiezen als trainer/spreker?' : 'Why choose me as a trainer/speaker?'}
          </Button>
        </Box>

        {/* Services Section */}
        <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 4, textAlign: 'center' }}>
          {t.offerTitle}
        </Typography>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          {/* Services Grid with expandable sections */}
          <Grid item xs={12}>
            <Box sx={{ p: 0 }}>
              <Grid container spacing={3}>
                {t.offerItems.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper elevation={2} 
                      sx={{ 
                        p: 3, 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: 3
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                          p: 1.5, 
                          borderRadius: '50%', 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2
                        }}>
                          {offerIcons[index]}
                        </Box>
                        <Typography variant="h6" fontWeight={600}>
                          {item}
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary',
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.main' }
                        }}
                        onClick={() => toggleExpand(item)}
                      >
                        {expandedServices[item] 
                          ? (language === 'nl' ? 'Toon minder' : 'Show less') 
                          : (language === 'nl' ? 'Toon meer details' : 'Show more details')}
                      </Typography>
                      <Collapse in={expandedServices[item]}>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {/* Content specific to each service */}
                            {index === 0 && (language === 'nl' 
                              ? 'Colleges en cursussen voor universitair en HBO-onderwijs, met diepgaande kennis en interactieve lesmethoden.'
                              : 'Lectures and courses for university and higher professional education, with in-depth knowledge and interactive teaching methods.')}
                            {index === 1 && (language === 'nl' 
                              ? 'Trainingen voor professionals gericht op specifieke vaardigheden en kennis die direct toepasbaar zijn in de praktijk.'
                              : 'Training for professionals focused on specific skills and knowledge that are directly applicable in practice.')}
                            {index === 2 && (language === 'nl' 
                              ? 'Inspirerende en informatieve presentaties voor conferenties, evenementen en bedrijfsbijeenkomsten.'
                              : 'Inspiring and informative presentations for conferences, events and company meetings.')}
                            {index === 3 && (language === 'nl' 
                              ? 'Professionele begeleiding van interactieve workshops met praktische oefeningen en ervaringsgerichte methoden.'
                              : 'Professional guidance of interactive workshops with practical exercises and experience-based methods.')}
                            {index === 4 && (language === 'nl' 
                              ? 'Begeleiding van individuele professionals bij hun persoonlijke en professionele ontwikkeling.'
                              : 'Guidance of individual professionals in their personal and professional development.')}
                            {index === 5 && (language === 'nl' 
                              ? 'Eenmalige colleges als gastdocent bij onderwijsinstellingen en organisaties.'
                              : 'One-off lectures as a guest teacher at educational institutions and organizations.')}
                          </Typography>
                        </Box>
                      </Collapse>
                      <Box sx={{ flexGrow: 1 }} />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
        
        {/* Expertise Section */}
        <Box sx={{ mb: 6, bgcolor: 'background.paper', p: 4, borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', mb: 3, fontWeight: 600 }}>
            {t.expertiseTitle}
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
            {t.expertiseDescription}
          </Typography>
          <Grid container spacing={3}>
            {t.expertiseItems.map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper elevation={0} sx={{ 
                  p: 2, 
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  borderRadius: 1
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <CheckIcon sx={checkIconStyle} />
                    <Typography variant="body1" fontWeight={500}>
                      {item}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
        
        {/* Benefits Section */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" align="center" sx={{ mb: 4, fontWeight: 600 }}>
            {language === 'nl' ? 'Voordelen voor uw organisatie' : 'Benefits for your organization'}
          </Typography>
          
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', boxShadow: 2, transition: 'transform 0.3s ease', '&:hover': { transform: 'translateY(-5px)' } }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {benefit.icon}
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {benefit.title}
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2">
                      {benefit.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Pricing Section */}
        <Box sx={{ mb: 6, textAlign: 'center', p: 4, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
            {t.pricingTitle}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 3 }}>
            {t.pricingDescription}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/contact')}
          >
            {language === 'nl' ? 'Vraag offerte aan' : 'Request quote'}
          </Button>
        </Box>

        <Divider sx={{ my: 6 }} />

        {/* "Why choose me" section */}
        <Box sx={{ mb: 6, p: 4, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>
            {language === 'nl' ? 'Waarom mij kiezen als spreker/docent?' : ''}
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <School color="primary" sx={{ fontSize: '2.5rem', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {language === 'nl' ? 'Academische achtergrond' : ''}
                </Typography>
                <Typography variant="body2">
                  {language === 'nl' 
                    ? 'Als universitair opgeleide professional breng ik diepgaande kennis en wetenschappelijk onderbouwde methoden.'
                    : ''}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <Psychology color="primary" sx={{ fontSize: '2.5rem', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {language === 'nl' ? 'Praktijkervaring' : ''}
                </Typography>
                <Typography variant="body2">
                  {language === 'nl' 
                    ? 'Jarenlange ervaring in het onderwijs en als spreker bij verschillende organisaties en evenementen.'
                    : ''}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <CastForEducation color="primary" sx={{ fontSize: '2.5rem', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {language === 'nl' ? 'Interactieve aanpak' : ''}
                </Typography>
                <Typography variant="body2">
                  {language === 'nl' 
                    ? 'Een dynamische aanpak die theorie verbindt met praktijk, voor maximale betrokkenheid en impact.'
                    : ''}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mb: 4, p: 4, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
            {t.ctaTitle}
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
            {t.ctaDescription}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => navigate('/waarom-ik#host')}
              sx={{ px: 4, py: 1.5 }}
            >
              {language === 'nl' ? 'Meer over mij' : 'More about me'}
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default Lesgeven; 